import React, { useState } from 'react'
import ReactionFeedback from './ReactionFeedback'
import {
  MessageComponentProps,
  MessageBubble,
  MessageBubbleText,
  FirebaseMessage
} from '@deal/chat-firebase'
import SellableReactions from '#src/components/SellableReactions'
import { Image } from '@deal/components'
import { Experiment } from '@deal/experiment-js'
import TerminationState from './TerminationState'
import styles from './styles.css'
import {
  SystemCuratedItemSurveyComponent,
  TerminateSystemCuratedItemSurveyComponent,
  SystemCuratedItemFragment,
  MessageRichCardType,
  ConsumerSellableReactionRating,
  SystemCuratedItemSurveyTerminationReason,
  StartSystemCuratedItemSurveyComponent,
  ConsumerSellableReactionSource,
  SaveConsumerSellableReactionComponent
} from '#src/generated/graphql'
import { getRecommendationSource } from './ReactionFeedback'
import { Link } from 'react-router-dom'
import { formatSellablePricing, getSellableUrl } from '#src/services/sellable'

interface State {
  sellableToShow: number
  consecutiveSkips: number
}

export default class ProductQuiz extends React.Component<MessageComponentProps, State> {
  public static handlesMessage = (message: FirebaseMessage) => {
    const { body } = message.parts[0]
    return (
      body.richCardType === MessageRichCardType.SYSTEM_CURATED_ITEM_SURVEY &&
      body.queryVariables.surveyId
    )
  }

  public constructor(props: MessageComponentProps) {
    super(props)

    this.state = { sellableToShow: 0, consecutiveSkips: 0 }
  }

  public render() {
    const { message, myself } = this.props
    const { sellableToShow, consecutiveSkips } = this.state
    const surveyId = message.parts[0].body.queryVariables.surveyId
    return (
      <StartSystemCuratedItemSurveyComponent>
        {startSystemCuratedItemSurvey => (
          <TerminateSystemCuratedItemSurveyComponent>
            {terminateSystemCuratedItemSurvey => (
              <SystemCuratedItemSurveyComponent
                variables={{ id: message.parts[0].body.queryVariables.surveyId }}
              >
                {({ data, loading }) => {
                  if (loading) {
                    return (
                      <MessageBubbleText message={message} myself={myself}>
                        Loading...
                      </MessageBubbleText>
                    )
                  } else if (data && data.systemCuratedItemSurvey) {
                    const { systemCuratedItems, ended, stats } = data.systemCuratedItemSurvey
                    const activeSystemCuratedItem = systemCuratedItems[sellableToShow]

                    return (
                      <div className={styles.messages}>
                        <MessageBubble message={message} className={styles.message}>
                          {ended && stats ? (
                            <TerminationState stats={stats} />
                          ) : activeSystemCuratedItem ? (
                            <SellableFeedbackCard
                              systemCuratedItem={activeSystemCuratedItem}
                              onProductRated={() => {
                                if (sellableToShow === 0) {
                                  startSystemCuratedItemSurvey({
                                    variables: {
                                      id: surveyId
                                    }
                                  })
                                }
                                // If there are no more sellables to show we must terminate the survey
                                //   Since setState is async we need to manually increment to be sure

                                if (!systemCuratedItems[sellableToShow + 1]) {
                                  terminateSystemCuratedItemSurvey({
                                    variables: {
                                      input: {
                                        id: surveyId,
                                        reason:
                                          SystemCuratedItemSurveyTerminationReason.REVIEWED_ALL
                                      }
                                    }
                                  })
                                }
                                this.setState(prevState => ({
                                  sellableToShow: prevState.sellableToShow + 1,
                                  consecutiveSkips: 0
                                }))
                              }}
                              onProductSkipped={() => {
                                if (sellableToShow === 0) {
                                  startSystemCuratedItemSurvey({
                                    variables: {
                                      id: surveyId
                                    }
                                  })
                                }

                                //   If that was the last item
                                if (!systemCuratedItems[sellableToShow + 1]) {
                                  terminateSystemCuratedItemSurvey({
                                    variables: {
                                      input: {
                                        id: surveyId,
                                        reason:
                                          SystemCuratedItemSurveyTerminationReason.REVIEWED_ALL
                                      }
                                    }
                                  })
                                  //   If the user skipped the first 3 sellables
                                } else if (consecutiveSkips === 2 && sellableToShow === 1) {
                                  terminateSystemCuratedItemSurvey({
                                    variables: {
                                      input: {
                                        id: surveyId,
                                        reason: SystemCuratedItemSurveyTerminationReason.SKIPPED
                                      }
                                    }
                                  })
                                  // If user has skipped 3 sellables in a row
                                } else if (consecutiveSkips === 2) {
                                  terminateSystemCuratedItemSurvey({
                                    variables: {
                                      input: {
                                        id: surveyId,
                                        reason:
                                          SystemCuratedItemSurveyTerminationReason.TERMINATED_EARLY
                                      }
                                    }
                                  })
                                }

                                this.setState(prevState => ({
                                  consecutiveSkips: prevState.consecutiveSkips + 1,
                                  sellableToShow: prevState.sellableToShow + 1
                                }))
                              }}
                            />
                          ) : null}
                        </MessageBubble>
                        {message.parts[1] && (
                          <MessageBubbleText message={message} myself={myself}>
                            {message.parts[1].body.text}
                          </MessageBubbleText>
                        )}
                      </div>
                    )
                  } else {
                    return (
                      <MessageBubbleText message={message} myself={myself}>
                        Error loading message!
                      </MessageBubbleText>
                    )
                  }
                }}
              </SystemCuratedItemSurveyComponent>
            )}
          </TerminateSystemCuratedItemSurveyComponent>
        )}
      </StartSystemCuratedItemSurveyComponent>
    )
  }
}

interface SellableFeedbackCardProps {
  systemCuratedItem: SystemCuratedItemFragment
  onProductRated: () => void
  onProductSkipped: () => void
}

const SellableFeedbackCard: React.SFC<SellableFeedbackCardProps> = ({
  systemCuratedItem,
  onProductRated,
  onProductSkipped
}) => {
  const { sellable, systemCuratedItemSource } = systemCuratedItem
  const { id, images, title } = sellable

  const pricing = formatSellablePricing(sellable, { round: false }, { preferDefaultVariant: false })
  const [rating, setRating] = useState<null | ConsumerSellableReactionRating>(null)

  return (
    /**
     * This experiment determines whether or not we allow the user to select a reason for their reaction
     * If aren't asking for reasons we skip rendering the ReactionFeedback and fire the saveConsumerSellableReaction mutation on reaction selection instead
     */
    <Experiment id="product-quiz-reason" default="off">
      {({ result }) => (
        <>
          {rating &&
            result === 'on' && (
              <ReactionFeedback
                recommendationSource={systemCuratedItemSource}
                sellableId={id}
                rating={rating}
                reactionSource={ConsumerSellableReactionSource.SYSTEM_CURATED_ITEM_SURVEY}
                onCompleted={() => {
                  onProductRated()
                  setRating(null)
                }}
              />
            )}

          <div className={styles.card}>
            <div className={styles.sellable}>
              {images.length > 0 && (
                <div className={styles.imageContainer}>
                  <Image src={images[0].url} height={100} width={100} className={styles.image} />{' '}
                </div>
              )}

              <div className={styles.details}>
                <div className={styles.title}>{title}</div>
                {pricing && (
                  <div className={styles.pricing}>
                    {pricing.price}
                    {pricing.msrp && <span className={styles.savings}>{pricing.msrp}</span>}
                  </div>
                )}
                <Link className={styles.viewLink} to={getSellableUrl(sellable)}>
                  View details
                </Link>
              </div>
            </div>
            <SaveConsumerSellableReactionComponent
              onCompleted={() => {
                onProductRated()
                setRating(null)
              }}
            >
              {saveConsumerSellableReaction => (
                <SellableReactions
                  onSellableRatingSelected={(rating: ConsumerSellableReactionRating) => {
                    result === 'on'
                      ? setRating(rating)
                      : saveConsumerSellableReaction({
                          variables: {
                            input: {
                              rating,
                              reasons: [],
                              sellableId: sellable.id,
                              recommendationSource: getRecommendationSource(
                                systemCuratedItemSource
                              ),
                              reactionSource:
                                ConsumerSellableReactionSource.SYSTEM_CURATED_ITEM_SURVEY
                            }
                          }
                        })
                  }}
                  onProductSkipped={onProductSkipped}
                />
              )}
            </SaveConsumerSellableReactionComponent>
          </div>
        </>
      )}
    </Experiment>
  )
}
